import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue, getSelectOptions } from './utils';
import EmptySection from './EmptySection';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';
import { useUpdateQuestionnaireStore } from '@/stores/useUpdateQuestionnaire';
import {
	PersonalInformation as PersonalInformationType,
	Maybe
} from '@/graphql/__generated__/credit-requests';
interface PersonalInformationProps {
	data: Maybe<PersonalInformationType> | undefined;
	title: string;
	isEditing: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	houseType = 'house_type',
	livesWithWhichFamilyMember = 'lives_with_which_family_member',
	transportationMethod = 'transportation_method',
	childDependants = 'child_dependants',
	otherFamilyDependants = 'other_family_dependants',
	otherFamilyDependantsExplanation = 'other_family_dependants_explanation'
}

const PersonalInformation: React.FC<PersonalInformationProps> = ({
	data,
	title,
	isEditing,
	inputErrors
}) => {
	const t = useTranslations('MicrocreditRequestQuestionnaire');
	const { personalInfo, updatePersonalInfo } = useUpdateQuestionnaireStore();
	const [formData, setFormData] = useState(personalInfo as PersonalInformationType);

	useEffect(() => {
		setFormData(personalInfo);
	}, [personalInfo]);

	const handleChange = (key: string, value: any) => {
		setFormData((prev) => ({
			...prev,
			[key]: value
		}));
		updatePersonalInfo({ [key]: value });
	};

	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'houseType'}
						value={formData['houseType'] || data?.houseType || ''}
						options={getSelectOptions('houseType', t)}
						isSelect={true}
						onChange={(newValue) => handleChange('houseType', newValue)}
						error={inputErrors?.[inputErrorMapping.houseType] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'livesWithWhichFamilyMember'}
						value={
							formData['livesWithWhichFamilyMember'] !== null
								? formData['livesWithWhichFamilyMember']
								: data?.livesWithWhichFamilyMember || ''
						}
						onChange={(newValue) => handleChange('livesWithWhichFamilyMember', newValue)}
						error={inputErrors?.[inputErrorMapping.livesWithWhichFamilyMember] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'transportationMethod'}
						value={formData['transportationMethod'] || data?.transportationMethod || ''}
						options={getSelectOptions('transportationMethod', t)}
						isSelect={true}
						onChange={(newValue) => handleChange('transportationMethod', newValue)}
						error={inputErrors?.[inputErrorMapping.transportationMethod] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'childDependants'}
						value={
							formData['childDependants'] !== null
								? formData['childDependants']
								: data?.childDependants || ''
						}
						type="number"
						onChange={(newValue) => handleChange('childDependants', newValue)}
						error={inputErrors?.[inputErrorMapping.childDependants] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'otherFamilyDependants'}
						value={
							formData['otherFamilyDependants'] !== null
								? formData['otherFamilyDependants']
								: data?.otherFamilyDependants || ''
						}
						type="number"
						onChange={(newValue) => handleChange('otherFamilyDependants', newValue)}
						error={inputErrors?.[inputErrorMapping.otherFamilyDependants] || ''}
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'otherFamilyDependantsExplanation'}
						value={
							formData['otherFamilyDependantsExplanation'] !== null
								? formData['otherFamilyDependantsExplanation']
								: data?.otherFamilyDependantsExplanation || ''
						}
						onChange={(newValue) => handleChange('otherFamilyDependantsExplanation', newValue)}
						error={inputErrors?.[inputErrorMapping.otherFamilyDependantsExplanation] || ''}
					/>
				</div>
			</>
		);
	};

	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			{data ? (
				<div className="flex flex-row w-full">
					<div className="flex flex-row flex-wrap w-full">
						{isEditing ? (
							editableFields()
						) : (
							<>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'houseType'}
										value={formatValue(data.houseType, t, 'houseType')}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'livesWithWhichFamilyMember'}
										value={formatValue(
											data.livesWithWhichFamilyMember,
											t,
											'livesWithWhichFamilyMember'
										)}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'transportationMethod'}
										value={formatValue(data.transportationMethod, t, 'transportationMethod')}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'childDependants'}
										value={formatValue(data.childDependants, t, 'childDependants')}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'otherFamilyDependants'}
										value={formatValue(data.otherFamilyDependants, t, 'otherFamilyDependants')}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'otherFamilyDependantsExplanation'}
										value={formatValue(
											data.otherFamilyDependantsExplanation,
											t,
											'otherFamilyDependantsExplanation'
										)}
									/>
								</div>
							</>
						)}
					</div>
				</div>
			) : (
				<EmptySection t={tForComponent(t)} />
			)}
		</div>
	);
};

export default PersonalInformation;
